import type { Writable } from "svelte/store";
import type { PixeldrainConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Pixeldrain does not send CORS headers. File calls go through /api/pixeldrain,
// which proxies https://pixeldrain.com/api. The API key is a request header.

export const prop_pd_sha256 = "sha256";
export const prop_pd_file_type = "file_type";
export const prop_pd_mode = "mode";

class FileSystemPixeldrain extends FS {
  supportsDirRename = true;

  constructor(config: PixeldrainConfig) {
    super(config);
  }

  get pixeldrainConfig(): PixeldrainConfig {
    return this.config as PixeldrainConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await pixeldrainList(this.pixeldrainConfig.apiKey, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the API key. Fetch through the
    // same-origin proxy and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/pixeldrain/download", "path", path);
    let xhr = await pdSend("GET", url, this.pixeldrainConfig.apiKey, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let mod = file instanceof File ? new Date(file.lastModified) : new Date();
    let url = mkURL("/api/pixeldrain/upload", "path", path, "modified", mod.toISOString());
    await pdSend("PUT", url, this.pixeldrainConfig.apiKey, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/pixeldrain/rename", "path", path, "newPath", newPath);
    await pdCall(this.pixeldrainConfig.apiKey, url);
  }

  async createDirectory(dirPath: string): Promise<void> {
    let url = mkURL("/api/pixeldrain/mkdir", "path", dirPath);
    await pdCall(this.pixeldrainConfig.apiKey, url);
  }

  async deleteFile(path: string): Promise<void> {
    let url = mkURL("/api/pixeldrain/delete", "path", path);
    await pdCall(this.pixeldrainConfig.apiKey, url);
  }
}

export async function pixeldrainAccount(apiKey: string): Promise<{ username: string }> {
  let js = await pdCall(apiKey, "/api/pixeldrain/test");
  return { username: js.username || "" };
}

async function pixeldrainList(apiKey: string, dir: string): Promise<any[][]> {
  let url = mkURL("/api/pixeldrain/list", "dir", dir);
  let js = await pdCall(apiKey, url);
  if (!Array.isArray(js)) {
    throw new Error("pixeldrain list failed");
  }
  return js;
}

async function pdCall(apiKey: string, url: string): Promise<any> {
  let rsp = await fetch(url, {
    headers: { "X-Pixeldrain-Key": apiKey },
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `pixeldrain ${rsp.status}`);
  }
  return js;
}

function pdSend(
  method: string,
  url: string,
  apiKey: string,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    xhr.setRequestHeader("X-Pixeldrain-Key", apiKey);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      reject(new Error(pdErrText(xhr)));
    };
    xhr.onerror = () => {
      reject(new Error("pixeldrain request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

function pdErrText(xhr: XMLHttpRequest): string {
  let text = "";
  if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `pixeldrain ${xhr.status}`;
}

export function makeFileSystemPixeldrain(c: PixeldrainConfig): FileSystemPixeldrain {
  return new FileSystemPixeldrain(c);
}
